import React from "react";
import { connect } from "react-redux";

import Panel from "../panels/Panel";
import FileExplorerLevel from "./FileExplorerLevel";

import {
    addFolder,
    deleteFiles,
    downloadFile,
    initialiseAllStructure,
    renameFiles,
    uploadFile,
} from "../../state/files/Folders";
import { IAppState } from "../../state/Store";

import File from "../../types/files/File";
import Folder from "../../types/files/Folder";
import { FileExplorerType } from "../../types/system/FileExplorer";

import "./FileExplorer.scss";
import Button from "../buttons/Button";
import Utilities from "../../foundation/Utility";
import ContextFragment from "../contextmenu/ContextFragment";
import ContextMenuItem from "../contextmenu/ContextMenuItem";
import { addAlert } from "../../state/system/Alerts";
import { AlertTypes } from "../../types/system/AlertEntry";

interface FileExplorerProps {
    value?: File;
    type: FileExplorerType;
    root: Folder;
    allStructure: File[];
    status: string;
    denyUpload?: boolean;

    onChange?: (file: File) => void;
}

interface FileExplorerState {
    loading: boolean;
}

class FileExplorer extends React.Component<
    FileExplorerProps,
    FileExplorerState
> {
    state = {
        loading: false,
    };

    async componentDidMount(): Promise<void> {
        try {
            this.setState({ loading: true });

            await initialiseAllStructure();
        } finally {
            this.setState({ loading: false });
        }
    }

    async rename(type: string, id: number) {
        const file = this.props.allStructure.find(
            f => f.fileType === type && f.id === id,
        );

        if (!file) return;

        const result = await addAlert({
            type: AlertTypes.Prompt,
            icon: "edit",
            title: "Rename File/Folder",
            okText: "Rename",
            prompts: [
                {
                    type: "text",
                    display: "Please enter a new name for the file/folder:",
                    value: file.name,
                },
            ],
        });

        if (result.response) {
            try {
                this.setState({ loading: true });
                const newName = result.promptResults?.[0]?.value;

                await renameFiles([{ file, name: newName }]);
            } finally {
                this.setState({ loading: false });
            }
        }
    }

    async delete(type: string, id: number, name: string) {
        const result = await addAlert({
            type: AlertTypes.Confirm,
            icon: "recycling",
            styleColor: "red",
            title: "Recycle File/Folder",
            text: `Are you sure you want to move the file/folder "${name}" to the recycle bin?`,
            okText: "Recycle",
        });

        if (result.response) {
            try {
                this.setState({ loading: true });

                await deleteFiles(
                    this.props.allStructure.filter(
                        f => f.fileType === type && f.id === id,
                    ),
                );
            } finally {
                this.setState({ loading: false });
            }
        }
    }

    async uploadFile(id: number) {
        try {
            this.setState({ loading: true });

            const file = await Utilities.uploadFile(
                ".tdf9,.tfx,.sof9,.sfx,.dof9,.zip",
                1,
            );

            if (!!file?.length)
                await uploadFile(id ?? this.props.root.id, file);
        } finally {
            this.setState({ loading: false });
        }
    }

    async downloadFile(type: string, id: number) {
        try {
            this.setState({ loading: true });

            let files: File[] = [];

            if (type === "Folder")
                files = this.props.allStructure.filter(f => f.folderId === id);
            else
                files = this.props.allStructure.filter(
                    f => f.id === id && f.fileType === type,
                );

            if (files.length) await downloadFile(files);
            else
                addAlert({
                    type: AlertTypes.Alert,
                    icon: "warning",
                    styleColor: "yellow",
                    title: "No Files to Download",
                    text: "There are no downloadable files selected.",
                });
        } finally {
            this.setState({ loading: false });
        }
    }

    async newFolder(id: number) {
        const result = await addAlert({
            type: AlertTypes.Prompt,
            icon: "create_new_folder",
            title: "New Folder Name",
            okText: "Create",
            prompts: [
                {
                    type: "text",
                    display: "Please enter a name for the new folder:",
                    value: "",
                },
            ],
        });

        if (result.response) {
            try {
                this.setState({ loading: true });
                const newName = result.promptResults?.[0]?.value;

                await addFolder(newName, id ?? this.props.root.id);
            } finally {
                this.setState({ loading: false });
            }
        }
    }

    render() {
        return (
            <>
                <Panel
                    className="file-explorer"
                    maxHeight="300px"
                    minHeight="300px"
                    containContent={true}
                    contextKey="file-explorer">
                    <FileExplorerLevel
                        type={this.props.type}
                        root={this.props.root}
                        parentId={0}
                        allData={this.props.allStructure}
                        selected={this.props.value}
                        onClick={item => this.props.onChange?.(item)}
                    />

                    {!this.props.denyUpload && (
                        <Button
                            icon="file_upload"
                            onClick={this.uploadFile.bind(this)}
                        />
                    )}

                    {this.state.loading && (
                        <div className="file-explorer-mask">
                            <div className="loader"></div>
                        </div>
                    )}
                </Panel>

                <ContextFragment name="file-explorer">
                    <ContextMenuItem
                        id="files_spacer_0"
                        separator={true}
                        contextKey="file-explorer;file-explorer-item"
                        order={0}
                    />
                    <ContextMenuItem
                        id="rename"
                        icon="edit"
                        name="Rename"
                        contextKey="file-explorer;file-explorer-item"
                        handler={(_, context) =>
                            this.rename(
                                context.getValue()?.split(":")[0],
                                Number(context.getValue()?.split(":")[1]),
                            )
                        }
                        disabledHandler={context => {
                            return (
                                !context.getValue() ||
                                context.getValue().endsWith(":Root")
                            );
                        }}
                        order={1}
                    />
                    <ContextMenuItem
                        id="delete"
                        icon="recycling"
                        name="Recycle"
                        contextKey="file-explorer;file-explorer-item"
                        handler={(_, context) =>
                            this.delete(
                                context.getValue()?.split(":")[0],
                                Number(context.getValue()?.split(":")[1]),
                                context.getValue()?.split(":")[2] ?? "",
                            )
                        }
                        disabledHandler={context => {
                            return (
                                !context.getValue() ||
                                context.getValue().endsWith(":Root")
                            );
                        }}
                        order={2}
                    />
                    <ContextMenuItem
                        id="upload"
                        icon="file_upload"
                        name="Upload File"
                        contextKey="file-explorer;file-explorer-item"
                        handler={(_, context) =>
                            this.uploadFile(
                                context.getValue() &&
                                    context.getValue().startsWith("Folder:")
                                    ? Number(context.getValue()?.split(":")[1])
                                    : null,
                            )
                        }
                        order={3}
                    />
                    <ContextMenuItem
                        id="download"
                        icon="file_download"
                        name="Download File"
                        contextKey="file-explorer;file-explorer-item"
                        handler={(_, context) =>
                            this.downloadFile(
                                context.getValue()?.split(":")[0],
                                Number(context.getValue()?.split(":")[1]),
                            )
                        }
                        disabledHandler={context => {
                            return (
                                !context.getValue() ||
                                context.getValue().endsWith(":Root")
                            );
                        }}
                        order={4}
                    />
                    <ContextMenuItem
                        id="folder"
                        icon="create_new_folder"
                        name="New Folder"
                        contextKey="file-explorer;file-explorer-item"
                        handler={(_, context) =>
                            this.newFolder(
                                context.getValue()
                                    ? Number(context.getValue()?.split(":")[1])
                                    : null,
                            )
                        }
                        disabledHandler={context => {
                            return (
                                !!context.getValue() &&
                                !context.getValue().startsWith("Folder:")
                            );
                        }}
                        order={4}
                    />
                </ContextFragment>
            </>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { fileExplorer, folders } = state.db;
    return {
        entry: fileExplorer.current,
        root: folders.root,
        allStructure: folders.allStructure,
        status: folders.allStructureStatus,
    };
};

const mapDispatchToProps = dispatch => {
    return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(FileExplorer);
